/**
 * Media URL Update Service
 *
 * Event-based service that notifies components when media URLs change
 * (e.g., when a blob URL is replaced with a Convex URL after upload completes).
 *
 * This allows LocalMediaContext to notify EditorContext when URLs change
 * without tight coupling between them.
 */

export interface UrlUpdateEvent {
  fileId: string;
  oldUrl: string;
  newUrl: string;
  fileName?: string;
}

type URLUpdateListener = (updates: UrlUpdateEvent[]) => void;

/**
 * Singleton service for managing media URL update events
 */
class MediaUrlUpdateServiceClass {
  private listeners: Set<URLUpdateListener> = new Set();
  private pendingUpdates: UrlUpdateEvent[] = [];
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Subscribe to URL update events
   *
   * @param listener - Callback function to receive URL update events
   * @returns Unsubscribe function
   */
  subscribe(listener: URLUpdateListener): () => void {
    this.listeners.add(listener);
    console.log(`[MediaUrlUpdateService] Listener subscribed. Total: ${this.listeners.size}`);

    return () => {
      this.listeners.delete(listener);
      console.log(`[MediaUrlUpdateService] Listener unsubscribed. Total: ${this.listeners.size}`);
    };
  }

  /**
   * Notify listeners of a URL update
   *
   * Updates are batched to avoid multiple rapid notifications
   *
   * @param fileId - The file ID that was updated
   * @param oldUrl - The previous URL (typically blob URL)
   * @param newUrl - The new URL (typically Convex URL)
   * @param fileName - Optional file name for logging
   */
  notifyUrlUpdate(fileId: string, oldUrl: string, newUrl: string, fileName?: string): void {
    // Don't notify if URLs are the same
    if (oldUrl === newUrl) {
      console.log(`[MediaUrlUpdateService] Skipping notification - URLs are identical`);
      return;
    }

    console.log(
      `[MediaUrlUpdateService] URL update queued for ${fileName || fileId}:`,
      `\n  Old: ${oldUrl.substring(0, 50)}...`,
      `\n  New: ${newUrl.substring(0, 50)}...`
    );

    // Add to pending updates
    this.pendingUpdates.push({ fileId, oldUrl, newUrl, fileName });

    // Batch updates with a small delay to avoid rapid successive notifications
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.flushUpdates();
    }, 100); // 100ms debounce
  }

  /**
   * Flush all pending updates to listeners
   */
  private flushUpdates(): void {
    if (this.pendingUpdates.length === 0) return;

    const updates = [...this.pendingUpdates];
    this.pendingUpdates = [];
    this.batchTimeout = null;

    console.log(`[MediaUrlUpdateService] Flushing ${updates.length} update(s) to ${this.listeners.size} listener(s)`);

    // Notify all listeners
    this.listeners.forEach((listener) => {
      try {
        listener(updates);
      } catch (error) {
        console.error(`[MediaUrlUpdateService] Error in listener:`, error);
      }
    });
  }

  /**
   * Convenience method for notifying upload completion
   *
   * @param fileId - The file ID that completed upload
   * @param blobUrl - The blob URL that was being used
   * @param convexUrl - The new Convex URL
   * @param fileName - Optional file name for logging
   */
  notifyUploadComplete(fileId: string, blobUrl: string, convexUrl: string, fileName?: string): void {
    console.log(`[MediaUrlUpdateService] Upload complete for ${fileName || fileId}`);
    this.notifyUrlUpdate(fileId, blobUrl, convexUrl, fileName);
  }

  /**
   * Get the number of active listeners (for debugging)
   */
  getListenerCount(): number {
    return this.listeners.size;
  }

  /**
   * Clear all listeners (for cleanup/testing)
   */
  clearListeners(): void {
    this.listeners.clear();
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    this.pendingUpdates = [];
  }
}

// Export singleton instance
export const MediaUrlUpdateService = new MediaUrlUpdateServiceClass();

// Also export the class for testing purposes
export { MediaUrlUpdateServiceClass };
